/**
 * 模块: TrendAnalysis数据管理Hook
 * 职责: 管理趋势分析的数据加载和状态
 * 限制: 文件<280行, 函数<30行
 */

import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import api, { brandNetworkAPI, marketShiftAPI } from '../../services/api';
import type { SKU, Period } from '../../types';
import { DEFAULT_METRICS } from './constants';

export const useTrendData = (id: string | undefined) => {
  const [loading, setLoading] = useState(false);
  const [sku, setSku] = useState<SKU | null>(null);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [trendData, setTrendData] = useState<any[]>([]);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(DEFAULT_METRICS);
  const [selectedPeriods, setSelectedPeriods] = useState<string[]>([]);
  const [chartType, setChartType] = useState<'line' | 'bar' | 'area'>('line');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [topBrandN, setTopBrandN] = useState<number>(50);
  const [networkData, setNetworkData] = useState<{ nodes: any[]; edges: any[]; stats?: { own_brand?: string } }>({ nodes: [], edges: [] });
  const [trajectoryData] = useState<any[]>([]); // 保留但不使用，避免破坏接口
  const [marketShiftData, setMarketShiftData] = useState<any[]>([]);

  const loadData = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const [skuRes, periodsRes] = await Promise.all([
        api.get(`/skus/${id}/`),
        api.get(`/skus/${id}/periods/`),
      ]);

      setSku(skuRes.data);
      setPeriods(periodsRes.data);

      const recentPeriods = periodsRes.data.slice(0, 4).map((p: Period) => p.id.toString());
      setSelectedPeriods(recentPeriods);
    } catch (error) {
      message.error('加载数据失败');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadTrendData = useCallback(async () => {
    if (!id || selectedPeriods.length === 0 || selectedMetrics.length === 0) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/skus/${id}/trends/`, {
        params: {
          periods: selectedPeriods.join(','),
          metrics: selectedMetrics.join(','),
        },
      });
      setTrendData(response.data);
    } catch (error) {
      message.error('加载趋势数据失败');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [id, selectedPeriods, selectedMetrics]);

  const loadNetworkData = useCallback(async () => {
    if (!id) return;
    try {
      const periodId = selectedPeriods[0] ? Number(selectedPeriods[0]) : undefined;
      const res = await brandNetworkAPI.forSku(Number(id), periodId, topBrandN);
      setNetworkData(res.data);
    } catch (e) {
      // ignore
    }
  }, [id, selectedPeriods, topBrandN]);

  const loadMarketShifts = useCallback(async () => {
    if (!id) return;
    try {
      const res = await marketShiftAPI.list({ sku_id: Number(id) });
      setMarketShiftData(res.data);
    } catch (e) {
      // ignore
    }
  }, [id]);

  const handleMetricChange = (checkedValues: string[]) => {
    if (checkedValues.length === 0) {
      message.warning('至少选择一个指标');
      return;
    }
    setSelectedMetrics(checkedValues);
  };

  const handlePeriodChange = (values: string[]) => {
    if (values.length === 0) {
      message.warning('至少选择一个周期');
      return;
    }
    setSelectedPeriods(values);
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    const validDates = dates && dates[0] && dates[1] ? [dates[0], dates[1]] as [Dayjs, Dayjs] : null;
    setDateRange(validDates);
    
    if (validDates) {
      const [start, end] = validDates;
      const filteredPeriods = periods
        .filter((p) => {
          const periodDate = dayjs(p.start_date);
          return periodDate.isAfter(start) && periodDate.isBefore(end);
        })
        .map((p) => p.id.toString());
      
      setSelectedPeriods(filteredPeriods);
    }
  };

  const handleExport = async () => {
    if (!id) return;
    
    try {
      const response = await api.post(
        `/skus/${id}/trends/export/`,
        {
          periods: selectedPeriods,
          metrics: selectedMetrics,
        },
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${sku?.name}_趋势分析.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      message.success('导出成功');
    } catch (error) {
      message.error('导出失败');
      console.error(error);
    }
  };

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id, loadData]);

  useEffect(() => {
    if (selectedPeriods.length > 0 && selectedMetrics.length > 0) {
      loadTrendData();
    }
  }, [selectedPeriods, selectedMetrics, loadTrendData]);

  useEffect(() => {
    if (id) {
      loadNetworkData();
      loadMarketShifts();
    }
  }, [id, selectedPeriods, topBrandN, loadNetworkData, loadMarketShifts]);

  return {
    loading,
    sku,
    periods,
    trendData,
    selectedMetrics,
    setSelectedMetrics: handleMetricChange,
    selectedPeriods,
    setSelectedPeriods: handlePeriodChange,
    chartType,
    setChartType,
    dateRange,
    setDateRange: handleDateRangeChange,
    topBrandN,
    setTopBrandN,
    networkData,
    trajectoryData,
    marketShiftData,
    loadData,
    handleExport,
  };
};

